import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Avatar, Button, IconButton, Chip, CircularProgress, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import {
  PhotoCameraRounded, EditRounded, StorefrontRounded, PinRounded, QrCode2Rounded,
  LogoutRounded, ChevronRightRounded, EventBusyRounded,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { colors, brand } from '../../theme/theme';
import { useProfile } from '../../hooks/useProfile';
import {
  getUser, getRole, getDisplayName, initials, clearSession, roleGradientKey, can,
} from '../../auth/session';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' };

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { updateDisplayName, setPin, uploadProfileImage } = useProfile();
  const fileRef = useRef<HTMLInputElement>(null);

  const role = getRole();
  const [name, setName] = useState(getDisplayName());
  const [avatarUrl, setAvatarUrl] = useState(getUser()?.profile_image_url || '');
  const [toast, setToast] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const gradKey = roleGradientKey(role?.role_name);
  const grad = gradKey === 'provider' ? brand.gradProvider : gradKey === 'supervisor' ? brand.gradSupervisor : brand.gradAdmin;

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const url = await uploadProfileImage(file); setAvatarUrl(url); setToast('Photo updated'); }
    catch (err: any) { setToast(err?.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const logout = () => { clearSession(); navigate('/login', { replace: true }); };

  const menu = [
    { key: 'edit', icon: <EditRounded />, title: 'Edit profile', sub: 'Your name and photo', onClick: () => setEditOpen(true), show: true },
    { key: 'business', icon: <StorefrontRounded />, title: 'Business details', sub: 'Address, billing & contact', onClick: () => navigate('/business'), show: can('can_edit_business') },
    { key: 'holidays', icon: <EventBusyRounded />, title: 'Set holidays', sub: 'Manage queue closures', onClick: () => navigate('/holidays'), show: can('can_set_holidays') },
    { key: 'pin', icon: <PinRounded />, title: 'Change PIN', sub: 'Update your 4-digit PIN', onClick: () => setPinOpen(true), show: false },
    { key: 'qr', icon: <QrCode2Rounded />, title: 'Share business QR', sub: 'Let customers find you', onClick: () => setQrOpen(true), show: true },
  ].filter((m) => m.show);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2.25, alignItems: 'start' }}>
      {/* Left: profile card */}
      <Box sx={{ ...card, p: 3, textAlign: 'center' }}>
        <Box sx={{ position: 'relative', width: 88, height: 88, mx: 'auto' }}>
          <Avatar src={avatarUrl || undefined} sx={{ width: 88, height: 88, fontSize: 30, background: grad }}>{initials(name)}</Avatar>
          <IconButton onClick={() => fileRef.current?.click()} disabled={uploading}
            sx={{ position: 'absolute', bottom: -4, right: -4, width: 34, height: 34, bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, '&:hover': { bgcolor: colors.gray50 } }}>
            {uploading ? <CircularProgress size={16} /> : <PhotoCameraRounded sx={{ fontSize: 18 }} />}
          </IconButton>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 18, mt: 1.5 }}>{name}</Typography>
        <Typography color="text.secondary">{getUser()?.country_code} {getUser()?.phone_number}</Typography>
        {role?.role_display_name && <Chip label={role.role_display_name} size="small" sx={{ mt: 1, bgcolor: colors.primaryLight, color: colors.primary, fontWeight: 600 }} />}
        <Button fullWidth startIcon={<LogoutRounded />} onClick={logout} sx={{ mt: 2.5, color: colors.error, bgcolor: '#FEF2F2', '&:hover': { bgcolor: '#FEE2E2' } }}>Logout</Button>
      </Box>

      {/* Right: menu */}
      <Box sx={{ ...card, overflow: 'hidden' }}>
        {menu.map((m, idx) => (
          <Box key={m.key} onClick={m.onClick}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: 2, cursor: 'pointer', borderBottom: idx < menu.length - 1 ? `1px solid ${colors.border}` : 'none', '&:hover': { bgcolor: colors.gray50 } }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '11px', bgcolor: colors.gray50, color: colors.primary, display: 'grid', placeItems: 'center' }}>{m.icon}</Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>{m.title}</Typography>
              <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{m.sub}</Typography>
            </Box>
            <ChevronRightRounded sx={{ color: colors.textTertiary }} />
          </Box>
        ))}
      </Box>

      {/* Edit profile */}
      {editOpen && <EditProfileDialog initialName={name} onClose={() => setEditOpen(false)} onSave={async (newName) => {
        await updateDisplayName(newName); setName(newName); setEditOpen(false); setToast('Profile updated');
      }} />}

      {/* Change PIN */}
      {pinOpen && <ChangePinDialog onClose={() => setPinOpen(false)} onSave={async (pin) => {
        await setPin(pin); setPinOpen(false); setToast('PIN updated');
      }} />}

      {/* Business QR */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Business QR</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {/* Encodes the business id — same value the mobile app encodes. */}
          <Box sx={{ display: 'inline-flex', p: 2, bgcolor: '#fff', borderRadius: '12px', border: `1px solid ${colors.border}`, boxShadow: '0 6px 18px rgba(15,23,42,.12)' }}>
            <QRCodeSVG value={role?.business_id || ''} size={200} level="M" />
          </Box>
          <Typography sx={{ mt: 2, fontWeight: 700 }}>{role?.business_name || 'Your business'}</Typography>
          <Typography variant="caption" color="text.secondary">Customers scan this to find your business.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => { navigator.clipboard?.writeText(role?.business_id || ''); setToast('Business ID copied'); }}>Copy ID</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

const EditProfileDialog: React.FC<{ initialName: string; onClose: () => void; onSave: (name: string) => Promise<void> }> = ({ initialName, onClose, onSave }) => {
  const [val, setVal] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    if (!val.trim()) return setErr('Enter your name.');
    setBusy(true); setErr(null);
    try { await onSave(val.trim()); } catch (e: any) { setErr(e?.response?.data?.message || 'Failed to save.'); setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Edit profile</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <TextField autoFocus fullWidth label="Display name" value={val} onChange={(e) => setVal(e.target.value)} sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={busy} onClick={save}>{busy ? <CircularProgress size={20} color="inherit" /> : 'Save'}</Button></DialogActions>
    </Dialog>
  );
};

const ChangePinDialog: React.FC<{ onClose: () => void; onSave: (pin: string) => Promise<void> }> = ({ onClose, onSave }) => {
  const [pin, setPinVal] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const clean = (v: string) => v.replace(/[^0-9]/g, '').slice(0, 4);
  const save = async () => {
    if (pin.length !== 4) return setErr('PIN must be 4 digits.');
    if (pin !== confirm) return setErr('PINs do not match.');
    setBusy(true); setErr(null);
    try { await onSave(pin); } catch (e: any) { setErr(e?.response?.data?.message || 'Failed to set PIN.'); setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Change PIN</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <TextField fullWidth label="New 4-digit PIN" type="password" value={pin} onChange={(e) => setPinVal(clean(e.target.value))} inputProps={{ inputMode: 'numeric' }} sx={{ mt: 1, mb: 2 }} />
        <TextField fullWidth label="Confirm PIN" type="password" value={confirm} onChange={(e) => setConfirm(clean(e.target.value))} inputProps={{ inputMode: 'numeric' }} />
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={busy} onClick={save}>{busy ? <CircularProgress size={20} color="inherit" /> : 'Set PIN'}</Button></DialogActions>
    </Dialog>
  );
};

export default ProfilePage;
